import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Stepper } from '../../components/wizard/Stepper.js';
import type { ConfigStatus } from '../../types/onboarding.js';

const STEP_PATHS = ['business', 'fiscal', 'timezone', 'logo'] as const;

export function Wizard() {
  const { t } = useTranslation('common');
  const { activeOrgId } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const { data, isPending } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });

  // Redirect to current step if user lands on /wizard root
  useEffect(() => {
    if (!data) return;
    if (loc.pathname === '/app/onboarding/wizard' || loc.pathname === '/app/onboarding/wizard/') {
      if (data.currentStep > 4) {
        // All steps complete — exit wizard
        nav('/app', { replace: true });
        return;
      }
      const idx = data.currentStep - 1;
      nav(`/app/onboarding/wizard/${STEP_PATHS[idx]}`, { replace: true });
    }
  }, [data, loc.pathname, nav]);

  if (!activeOrgId) return <Navigate to="/app/onboarding/create-org" replace />;
  if (isPending) return <p className="m-8 text-sm">{t('loading')}</p>;
  if (!data) return <p className="m-8 text-sm">Error</p>;

  const steps = [
    { label: 'Información', done: data.step1, current: loc.pathname.endsWith('/business') },
    { label: 'Datos fiscales', done: data.step2, current: loc.pathname.endsWith('/fiscal') },
    { label: 'Zona horaria', done: data.step3, current: loc.pathname.endsWith('/timezone') },
    { label: 'Logo', done: data.step4, current: loc.pathname.endsWith('/logo') },
  ];

  return (
    <div className="mx-auto mt-8 max-w-2xl rounded border bg-white p-6">
      <h1 className="mb-4 text-xl font-semibold">Configuración inicial</h1>
      <Stepper steps={steps} />
      <Outlet />
    </div>
  );
}
